/* eslint-disable camelcase */
import axios from 'axios';
import { drawerMobileNavWhiteList } from './constant';
import { UserType } from '@store/auth';

const useLayout = () => {
    const route = useRoute();
    const router = useRouter();
    const authStore = useAuthStore();
    const contentStore = useContentStore();
    const analyticsStore = useAnalyticsStore();
    const autoReserveStore = useAutoReserveStoreIfInstalled();
    const { isAirplaneTicket, isMrbilit } = useWhiteLabel();
    const { isWebApp } = useDevice();
    const siteConfig = useSiteConfigs();
    const i18n = useI18n();
    const { loginByToken } = authStore;
    const { termsStatus, currentUserType } = storeToRefs(authStore);
    const searchFormStore = useSearchFormStore();
    const { reserveType } = storeToRefs(searchFormStore);
    const layoutStore = useLayoutStore();
    const { load } = useScriptTag('/js/clarity.js', undefined, {
        manual: true,
    });
    const { load: goftino, scriptTag } = useScriptTag(
        '/js/goftino.js',
        undefined,
        {
            manual: true,
        }
    );

    watch(currentUserType, value => {
        if (value === UserType.Organization) {
            goftino();
            window.addEventListener('goftino_ready', () => {
                const w = window as any;
                w.Goftino.setUser({
                    name: authStore.accountName,
                });
            });
        } else if (scriptTag.value) {
            setTimeout(() => location.reload(), 100);
        }
    });

    onServerPrefetch(async () => {
        layoutStore.isWebApp = !!route.query.webAppLayout;
        await contentStore.loadBanners();
    });

    if (isMrbilit.value) {
        useSeoMeta({
            title: siteConfig.seoMeta.title,
            ogUrl: `https://mrbilit.com${route.fullPath}`,
            description: siteConfig.seoMeta.description,
            ogDescription:
                'مِستربلیط وب سایت و اپلیکیشنی برای خرید ارزان و اینترنتی بلیط هواپیما، بلیط قطار و بلیط اتوبوس به صورت ساده، سریع و ارزان است. کافیست برای رزرو بلیط، مسیر و تاریخ را مشخص کنید تا مِستربلیط تمام موارد را جستجو کرده و بلیط ارزان به مقصد دلخواه شما را پیشنهاد دهد.',
            ogTitle: siteConfig.seoMeta.title,
            ogSiteName: 'مِستربلیط',
            ogImage: 'https://mrbilit.com/icon.png',
        });
    } else if (isAirplaneTicket.value) {
        i18n.setLocale('apt');
        useSeoMeta({
            title: siteConfig.seoMeta.title,
            ogUrl: `https://airplaneticket.ir${route.fullPath}`,
            description: siteConfig.seoMeta.description,
            ogDescription: siteConfig.seoMeta.description,
            ogTitle: siteConfig.seoMeta.title,
            ogSiteName: 'ایرپلین تیکت',
            ogImage: '/airplaneticket/apple-touch-icon.png',
        });
        useServerHead({
            link: [
                {
                    rel: 'shortcut icon',
                    type: 'image/x-icon',
                    href: '/airplaneticket/favicon.ico',
                },
                {
                    rel: 'apple-touch-icon',
                    sizes: '180x180',
                    href: '/airplaneticket/apple-touch-icon.png',
                },
                {
                    rel: 'icon',
                    type: 'image/png',
                    sizes: '32x32',
                    href: '/airplaneticket/favicon-32x32.png',
                },
                {
                    rel: 'icon',
                    type: 'image/png',
                    sizes: '16x16',
                    href: '/airplaneticket/favicon-16x16.png',
                },
            ],
        });
    }
    const showDrawer = computed(() =>
        drawerMobileNavWhiteList.some(path => route.path.startsWith(path))
    );
    const { open } = useModal({
        component: defineAsyncComponent(
            () => import('@components/layout/auth/OrganizationalSaleTerms.vue')
        ),
        modalAttrs: {
            permanent: true,
        },
    });
    watch(termsStatus, value => {
        if (value === 'notAccepted') {
            open();
        }
    });

    const autoReserveGiftInit = () => {
        const { autoreserve_gift, autoreserve_mac } = route.query;
        if (autoreserve_gift && autoreserve_mac) {
            authStore.giftsSetter({
                mac: autoreserve_mac.toString(),
                code: autoreserve_gift.toString(),
            });

            sessionStorage.setItem(
                'autoReserveGifts',
                JSON.stringify({
                    autoreserveGift: autoreserve_gift,
                    autoreserveMac: autoreserve_mac,
                })
            );
        } else {
            const { autoreserveGift, autoreserveMac } = JSON.parse(
                sessionStorage.getItem('autoReserveGifts') || '{}'
            );
            if (autoreserveGift && autoreserveMac) {
                authStore.giftsSetter({
                    mac: autoreserveMac.toString(),
                    code: autoreserveGift.toString(),
                });
            }
        }
    };
    if (isWebApp.value) {
        useHead({
            htmlAttrs: {
                class: 'app-web-view',
            },
        });
    }
    onBeforeMount(async () => {
        if (layoutStore.isWebApp) {
            sessionStorage.setItem('mobile-web-view', 'true');
        } else {
            layoutStore.isWebApp = !!sessionStorage.getItem('mobile-web-view');
        }
        authStore.initSession();
        axios.interceptors.request.use(
            config => {
                if (
                    config.url?.split('/')[2].includes('mrbilit') ||
                    config.url?.split('/')[2].includes('atighgasht')
                ) {
                    const { token } = authStore;
                    const smId = getSmId(reserveType.value);
                    if (token && !config.headers.Authorization) {
                        config.headers.Authorization = `Bearer ${token}`;
                    }
                    const { uuid, sessionId } = authStore;
                    if (uuid) {
                        config.headers['X-PlayerID'] = uuid;
                    }
                    if (smId) {
                        config.headers['Safarmarket-ID'] = smId;
                    }
                    if (sessionId) {
                        config.headers.SessionId = sessionId;
                    }
                }
                return config;
            },
            error => Promise.reject(new Error(error))
        );
        //#region login admin with url logic
        const username = route.query.login_username as string;
        const token = route.query.login_token as string;
        if (username && token) {
            authStore.adminSession = true;
            await loginByToken({ token, username });
            router.replace('/');
        }
        if (isWebApp.value) {
            window.HandleWebAppRoute = (path: string) => {
                if (path === 'payment-success') {
                    if (authStore.loggedIn) {
                        router.push('/panel/reserves');
                    } else {
                        router.push('/');
                    }
                } else if (path === 'payment-error') {
                    router.push('/');
                } else if (path === 'research') {
                    router.push(localStorage.viewUrl || '/');
                } else {
                    router.push(path);
                }
            };
        }
        //#endregion

        authStore.initUser(isWebApp.value);
    });
    onMounted(() => {
        autoReserveGiftInit();
        contentStore.mounted = true;
        layoutStore.isMounted = true;
        window.webengage?.onReady(() => {
            window.webengage?.options('webpush.registerServiceWorker', false);
        });
        if (!route.path.startsWith('/reserve')) {
            autoReserveStore?.resetAutoReserve();
        }
        LocalStorageUtils.revalidateStorage();
        contentStore.init();
        const calendar = localStorage.getItem('calendar');
        const locale = localStorage.getItem('locale');
        if (calendar && calendar !== dayConfig.calendar) {
            switchCalendar();
        }
        if (locale && locale !== dayConfig.locale) {
            switchCalendar();
        }

        const { query, path } = route;
        if (
            query.utm_campaign ||
            query.utm_medium ||
            query.utm_source ||
            query.utm_creative ||
            query.utm_content ||
            query.utm_key ||
            query.utm_service ||
            query.tatoken
        ) {
            authStore.sendUTM({
                utmCampaign: query.utm_campaign?.toString() || '',
                utmMedium: query.utm_medium?.toString() || '',
                utmSource: query.utm_source?.toString() || '',
                utmCreative: query.utm_creative?.toString() || '',
                utmContent: query.utm_content?.toString() || '',
                utmKey:
                    query.utm_key?.toString() ||
                    '' ||
                    query.tatoken?.toString() ||
                    '',
                utmServiceType: query.utm_service_type?.toString() || '',
                url: path,
                referrer: document.referrer,
            });
        } else if (query.ezam_auth_token) {
            authStore.loginByThirdParty({
                whiteLabel: 'ezam',
                token: query.ezam_auth_token.toString(),
            });
            history.replaceState({}, '', route.path);
        }
        analyticsStore.trackPosthog('user-date-time', {
            date: new Date().toISOString(),
        });
        const isClarityEnabled = localStorage.getItem('isClarityEnabled');
        if (isClarityEnabled === 'true') {
            load();
        } else if (isClarityEnabled === null) {
            const enableClarity = Math.random() <= 0.05;
            localStorage.setItem('isClarityEnabled', enableClarity.toString());
            if (enableClarity) {
                load();
            }
        }

        if (isWebApp.value) {
            useGtm()?.trackEvent({ event: 'web-app' });
        }
    });

    return { showDrawer };
};

export default useLayout;
